"use client";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { ColumnDef } from "@tanstack/react-table";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { getInitials } from "@/lib/utils";
import { DataBerkas, DataPendaftaran } from "@/types";
import { ArrowUpDown, FileSearch } from "lucide-react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { getMasterSystem } from "@/services/getData";

export const columns: ColumnDef<DataPendaftaran>[] = [
  {
    id: "select",
    header: ({ table }) => (
      <div className="px-1">
        <Checkbox
          checked={
            table.getIsAllPageRowsSelected() ||
            (table.getIsSomePageRowsSelected() && "indeterminate")
          }
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
          aria-label="Select all"
          className="border-slate-300 dark:border-slate-700 data-[state=checked]:bg-primary"
        />
      </div>
    ),
    cell: ({ row }) => (
      <div className="px-1">
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          aria-label="Select row"
          className="border-slate-300 dark:border-slate-700 data-[state=checked]:bg-primary"
        />
      </div>
    ),
    enableSorting: false,
    enableHiding: false,
  },
  {
    accessorKey: "verifikasi",
    header: () => (
      <div className="text-center font-semibold text-slate-600 dark:text-slate-300">
        Aksi
      </div>
    ),
    cell: ({ row }) => {
      const nomorPendaftaran = row.original?.nomor_pendaftaran;

      // 1. Ambil URL (path + parameter pencarian/filter) saat ini
      const currentPath = window.location.pathname;
      const currentSearch = window.location.search;

      // 2. Encode agar aman disisipkan ke dalam URL tujuan
      const returnTo = encodeURIComponent(`${currentPath}${currentSearch}`);

      return (
        <div className="flex justify-center">
          <Link
            target="_blank"
            // 3. Sisipkan parameter returnTo ke dalam link tujuan
            to={`/verifikator/skl/verifikasi-skl-peserta/view?no=${nomorPendaftaran}&returnTo=${returnTo}`}
          >
            <Button
              variant="outline"
              size="sm"
              className="h-8 px-3 shadow-sm hover:bg-primary hover:text-white transition-all dark:border-slate-700 dark:bg-slate-900"
            >
              <FileSearch className="w-3.5 h-3.5 mr-1.5" />
              Verifikasi
            </Button>
          </Link>
        </div>
      );
    },
  },
  {
    accessorKey: "status_validasi",
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          size="sm"
          className="-ml-3 h-8 font-semibold text-slate-600 dark:text-slate-300"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Status SKL
          <ArrowUpDown className="w-3.5 h-3.5 ml-2 opacity-50" />
        </Button>
      );
    },
    cell: ({ row }) => {
      const { data: system } = useQuery({
        queryKey: ["masterSystem"],
        queryFn: getMasterSystem,
      });

      const isWaktuSanggahBerkas =
        system && system[0]?.is_waktu_sanggah_berkas_daftarkembali;

      const dataBerkasSkl = row.original?.data_berkas?.find(
        (berkas: DataBerkas) =>
          berkas?.sys_berkas?.jenis_berkas?.slug_jenis_berkas ===
          "Ijazah_SMA_SMK",
      );
      const isSudahSanggahBerkas = !!dataBerkasSkl?.path_sanggah_berkas;

      const rawValue = dataBerkasSkl?.is_valid;

      // Cek null/undefined secara eksplisit terlebih dahulu
      if (rawValue === null || rawValue === undefined) {
        return (
          <Badge
            variant="outline"
            className="bg-slate-500/10 text-slate-600 border-slate-500/20 font-medium"
          >
            Belum Dicek
          </Badge>
        );
      }

      // Konversi ke number untuk menghindari konflik tipe data boolean vs number
      const status = Number(rawValue);

      if (isWaktuSanggahBerkas && isSudahSanggahBerkas) {
        return (
          <Badge
            variant="outline"
            className="bg-yellow-500/10 text-yellow-600 border-yellow-500/20 font-medium"
          >
            Sudah Sanggah
          </Badge>
        );
      }

      if (isWaktuSanggahBerkas && !isSudahSanggahBerkas) {
        return <Badge variant="outline">Belum Sanggah</Badge>;
      }

      if (status === 1) {
        return (
          <Badge
            variant="outline"
            className="bg-emerald-500/10 text-emerald-600 border-emerald-500/20 font-medium"
          >
            Sudah Valid
          </Badge>
        );
      }

      // Jika status === 0
      return (
        <Badge
          variant="outline"
          className="bg-destructive/10 text-destructive border-destructive/20 font-medium"
        >
          Ditolak
        </Badge>
      );
    },
  },
  {
    accessorKey: "catatan_verifikator",
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          size="sm"
          className="-ml-3 h-8 font-semibold text-slate-600 dark:text-slate-300"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Catatan Verifikator
          <ArrowUpDown className="w-3.5 h-3.5 ml-2 opacity-50" />
        </Button>
      );
    },
    cell: ({ row }) => {
      const berkasSkl = row.original?.data_berkas?.find((berkas) => {
        return (
          berkas.sys_berkas?.jenis_berkas?.slug_jenis_berkas == "Ijazah_SMA_SMK"
        );
      });

      return (
        <div>
          <h1 className="font-semibold">
            {berkasSkl?.user_verifikator?.nama_lengkap}
          </h1>
          <p className="italic">{berkasSkl?.catatan_verifikator}</p>
        </div>
      );
    },
  },
  {
    accessorKey: "nomor_pendaftaran",
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          size="sm"
          className="-ml-3 h-8 font-semibold text-slate-600 dark:text-slate-300"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          No. Pendaftaran
          <ArrowUpDown className="w-3.5 h-3.5 ml-2 opacity-50" />
        </Button>
      );
    },
    cell: ({ row }) => (
      <div className="font-mono text-sm text-slate-700 dark:text-slate-300">
        {row.getValue("nomor_pendaftaran")}
      </div>
    ),
  },
  {
    accessorKey: "data_siswa.nama_lengkap",
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          size="sm"
          className="-ml-3 h-8 font-semibold text-slate-600 dark:text-slate-300"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Nama Lengkap
          <ArrowUpDown className="w-3.5 h-3.5 ml-2 opacity-50" />
        </Button>
      );
    },
    cell: ({ row }) => {
      const nama = row.original.data_siswa?.nama_lengkap || "";
      return (
        <div
          className="font-medium text-slate-900 dark:text-slate-100 capitalize truncate max-w-[200px]"
          title={nama}
        >
          {nama || "-"}
        </div>
      );
    },
  },
  {
    accessorKey: "data_siswa.sekolah.nama_sekolah",
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          size="sm"
          className="-ml-3 h-8 font-semibold text-slate-600 dark:text-slate-300"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Asal Sekolah
          <ArrowUpDown className="w-3.5 h-3.5 ml-2 opacity-50" />
        </Button>
      );
    },
    cell: ({ row }) => (
      <div className="text-muted-foreground text-sm truncate max-w-[180px]">
        {row.original.data_siswa?.sekolah?.nama_sekolah || "-"}
      </div>
    ),
  },
  {
    accessorKey: "unitkerja.nama",
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          size="sm"
          className="-ml-3 h-8 font-semibold text-slate-600 dark:text-slate-300"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Prodi Pilihan
          <ArrowUpDown className="w-3.5 h-3.5 ml-2 opacity-50" />
        </Button>
      );
    },
    cell: ({ row }) => (
      <div className="text-sm truncate max-w-[150px]">
        {row.original.unitkerja?.nama || "-"}
      </div>
    ),
  },
  {
    accessorKey: "data_siswa.email",
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          size="sm"
          className="-ml-3 h-8 font-semibold text-slate-600 dark:text-slate-300"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Email
          <ArrowUpDown className="w-3.5 h-3.5 ml-2 opacity-50" />
        </Button>
      );
    },
    cell: ({ row }) => (
      <div className="text-xs text-muted-foreground">
        {row.original.data_siswa?.email || "-"}
      </div>
    ),
  },
  {
    accessorKey: "data_siswa.jenis_kelamin",
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          size="sm"
          className="-ml-3 h-8 font-semibold text-slate-600 dark:text-slate-300"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          L/P
          <ArrowUpDown className="w-3.5 h-3.5 ml-2 opacity-50" />
        </Button>
      );
    },
    cell: ({ row }) => {
      const jk = row.original.data_siswa?.jenis_kelamin as string | null;

      return (
        <div className="text-center font-medium text-slate-500">
          {jk === "Laki-laki" || jk === "L"
            ? "L"
            : jk === "Perempuan" || jk === "P"
              ? "P"
              : "-"}
        </div>
      );
    },
  },
  {
    accessorKey: "data_siswa.path_foto",
    header: () => (
      <div className="text-center font-semibold text-slate-600 dark:text-slate-300">
        Foto
      </div>
    ),
    cell: ({ row }) => {
      const nama = row.original.data_siswa?.nama_lengkap;
      const foto = row.original.data_siswa?.path_foto;
      return (
        <div className="flex justify-center">
          <Avatar className="h-9 w-9 border border-slate-200 dark:border-slate-800 shadow-sm">
            <AvatarImage
              src={foto || ""}
              alt={nama || "Foto"}
              className="object-cover"
            />
            <AvatarFallback className="text-[10px] bg-slate-100 dark:bg-slate-800">
              {getInitials(nama || "")}
            </AvatarFallback>
          </Avatar>
        </div>
      );
    },
    enableSorting: false,
  },
];
